import { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useEsri } from '../../esriHooks'

import { getView, getAllViews } from '../selectors'

import { setMapAndView } from '../actions'

export const useMapIsUpdating = (mapId) => {
  const view = useSelector(getView(mapId))
  const [updating, setUpdating] = useState(view && view.updating)
  useEffect(() => {
    if (view) {
      view.watch("updating", (isUpdating) => {
        setUpdating(isUpdating)
      })
    }
  }, [view])

  return updating
}

export const useViewScale = (mapId) => {
  const view = useSelector(getView(mapId))
  const [scale, setScale] = useState(view && view.scale)
  useEffect(() => {
    if (view) {
      view.watch("scale", (newScale, oldScale) => {
        setScale(newScale)
      })
    }
  }, [view])

  return scale
}

export const useMap = (id) => {
  const dispatch = useDispatch()
  const [ready, { Map, MapView }] = useEsri(['esri/Map', 'esri/views/MapView'])

  useEffect(() => {
    if (ready) {
      const map = new Map({
        basemap: "streets-navigation-vector", // Basemap layer service
      });

      const view = new MapView({
        map: map,
        center: [-78.938501, 33.933995], // Longitude, latitude of horry county
        zoom: 9, // Zoom level to see most of the county
        container: `mym-map-${id}`, // Div element
        popup: {
          dockEnabled: true,
          dockOptions: {
            // Disables the dock button from the popup
            buttonEnabled: false,
            // Ignore the default sizes that trigger responsive docking
            breakpoint: true,
            position: 'top-right'
          }
        }
      });

      dispatch(setMapAndView(id, map, view))

      return () => {
        dispatch(setMapAndView(id))
      }
    }
  }, [ready, id])
}

export const useSyncMaps = (expectedMapCount) => {
  const views = useSelector(getAllViews)

  useEffect(() => {
    // Once we have all of them and watchUtils
    if (views.length === expectedMapCount) {
      // set up linkage
      let activeView;
      const sync = (source) => {
        if (!activeView || !activeView.viewpoint || activeView !== source) {
          return;
        }

        for (const v of views) {
          if (v !== activeView) {
            v.viewpoint = activeView.viewpoint
          }
        }
      }
      const onInteractionOrAnimation = (v) => () => {
        activeView = v
        sync(activeView)
      }

      const handles = []
      for (const v of views) {
        handles.push(v.watch(['interacting', 'animation'], onInteractionOrAnimation(v)))
        handles.push(v.watch('viewpoint', () => sync(v)))
      }
      return () =>  handles.forEach(h => h.remove())
    }
  }, [views, expectedMapCount])
}

export const useFocusMap = (mapId, geometry) => {
  const [ready, { Polygon, Graphic }] = useEsri(['esri/geometry/Polygon', 'esri/Graphic'])
  const view = useSelector(getView(mapId))
  
  useEffect(() => {
    if (ready && view && geometry) {
      const graphic = new Graphic({
        geometry: new Polygon(geometry),
        symbol: {
          type: "simple-fill",  // autocasts as new SimpleFillSymbol()
          color: [ 35,35,35, 0.0],
          style: "solid",
          outline: {  // autocasts as new SimpleLineSymbol()
            color: [ 35,35, 35, 0.9 ],
            width: 4
          }
        }
      })
      view.graphics.removeAll()
      view.graphics.add(graphic)
      view.goTo({ 
        target: graphic
      })
    }
  }, [view, geometry, ready, Polygon, Graphic])
}